import { Component, inject, signal, type WritableSignal } from '@angular/core';
import {Navbar} from '../../../../shared/components/navbar/navbar';
import {Button} from 'primeng/button';
import {HeaderCard} from '../../../../shared/components/header-card/header-card';
import { StatementService } from '../../../../services/statement.service';
import type {
  CreateStatementPayload,
  Statement,
  StatementAccount,
  StatementMetrics, UpdateStatementPayload
} from '../../../../interfaces/statement';
import {CommonModule} from '@angular/common';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import dayjs from 'dayjs';
import {Divider} from 'primeng/divider';
import {ChartModule} from 'primeng/chart';
import {ChartConfiguration} from 'chart.js';
import {TableStatementAccount} from '../../../../shared/tables/table-statement-account/table-statement-account';
import {Dialog} from 'primeng/dialog';
import {InputText} from 'primeng/inputtext';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputNumberModule} from 'primeng/inputnumber';
import {Select} from 'primeng/select';
import {Textarea} from 'primeng/textarea';
import {DatePickerModule} from 'primeng/datepicker';
import {MessageService} from 'primeng/api';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize} from 'rxjs';
import {Message} from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ActivatedRoute, Router } from '@angular/router';
import { doughnutChartDefaultOptions } from '../../../../interfaces/charts';

@Component({
  selector: 'app-statements-list',
  imports: [
    CommonModule,
    Navbar,
    Button,
    HeaderCard,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Divider,
    ChartModule,
    TableStatementAccount,
    Dialog,
    ReactiveFormsModule,
    InputNumberModule,
    Select,
    Textarea,
    DatePickerModule,
    InputText,
    Message,
    SkeletonModule
],
  templateUrl: './statements-list.html',
  styleUrl: './statements-list.css'
})
export class StatementsList {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _statementService: StatementService = inject(StatementService);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _metrics$ = this._statementService.metrics();

  public tabSelected: WritableSignal<string> = signal<string>('general');

  public metrics: WritableSignal<StatementMetrics|null> = signal<StatementMetrics|null>(null);

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public accountsChartData: WritableSignal<any> = signal<any>(null);
  public accountOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...doughnutChartDefaultOptions});

  public statementDialog: WritableSignal<boolean> = signal<boolean>(false);
  public statementDialogTitle: WritableSignal<string> = signal<string>('');

  public statementForm: FormGroup = this._fb.group({
    id: [null],
    cashier_account_id: [null, [Validators.required]],
    description: [null],
    value: [null, [Validators.required]],
    launch_at: [dayjs().toDate(), [Validators.required]],
    docs: [[]],
    responsible: [{ value: null, disabled: true }],
    created_at: [{ value: null, disabled: true }],
  });

  public ngOnInit(): void {
    this._getMetrics();

    this._activatedRoute.queryParams.subscribe((params) => this.updateParams(params));
    const { tab } = this._activatedRoute.snapshot.queryParams;

    if (tab) {
      this.tabSelected.set(tab);
    }
  }

  public updateParams(params: any): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  private _getMetrics(): void {
    this._toggleLoading();
    this._metrics$
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.metrics.set(response.data);
            this._mountAccountsChartData(response?.data?.contas_vinculadas?.items ?? []);
            this._mountAccountChartOptions();
          }
        }
      })
  }

  private _mountAccountsChartData(accounts: StatementAccount[]) {
    accounts.forEach((account) => (this.accountsChartData.update(value => ({
      ...value,
      [account.id!]: {
        labels: ['Entradas', 'Saídas'],
        datasets: [
          {
            label: '',
            data: [
              account.entradas_periodo,
              account.saidas_periodo
            ]
          }
        ]
      }
    }))))
  }

  private _mountAccountChartOptions(): void {
    this.accountOptions.set({
      ...doughnutChartDefaultOptions,
      plugins: {
        ...doughnutChartDefaultOptions!.plugins,
        legend: { display: false },
      }
    });
  }

  public formatMetricsPeriod(): string {
    const start = this.metrics()?.period.start ? dayjs(this.metrics()?.period.start).format('DD/MM/YYYY') : '';
    const end = this.metrics()?.period.end ? dayjs(this.metrics()?.period.end).format('DD/MM/YYYY') : '';

    if (!start && !end) {
      return '';
    }

    return `${start} - ${end}`;
  }

  public getChartData(account: StatementAccount) {
    return {
      labels: ['Entradas', 'Saídas'],
      datasets: [
        {
          label: '',
          data: [
            account.entradas_periodo,
            account.saidas_periodo
          ]
        }
      ]
    }
  }

  public getChartOptions(): ChartConfiguration["options"] {
    return {
      ...doughnutChartDefaultOptions,
      plugins: {
        ...doughnutChartDefaultOptions!.plugins,
        legend: { display: false },
      }
    }
  }

  public closeDialog(): void {
    this.statementDialog.set(false);
    this.statementForm.reset();
  }

  public openDialog(statement?: Statement): void {
    this.statementDialog.set(true);

    if (statement) {
      this.statementForm.patchValue({
        ...statement,
        launch_at: statement.launch_at ? dayjs(statement.launch_at).toDate() : null,
        created_at: statement.created_at ? dayjs(statement.created_at).toDate() : null,
        responsible: statement.responsible ? statement.responsible.name : null
      });
      this.statementDialogTitle.set('Atualizar Lançamento de Caixa');
      return;
    }

    this.statementDialogTitle.set('Adicionar Lançamento de Caixa');
  }

  public submitDialog(): void {
    if (!this.statementForm.valid) {
      this.statementForm.markAllAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Preencha todos os campos obrigatórios',
        life: 3000
      });

      return;
    }

    const data = this.statementForm.getRawValue();
    if (data.id) {
      this.edit(data.id, data)
    }
  }

  public create(payload: CreateStatementPayload): void {
    this._toggleLoading();
    this._statementService.post(payload)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });

            this.closeDialog();
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao criar um lançamento no extrato',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public edit(id: string, payload: UpdateStatementPayload): void {
    this._toggleLoading();
    this._statementService.put(id, payload)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });

            this.closeDialog();
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao atualizar um lançamento no extrato',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public export(id: string): void {
    console.log({id})
  }

  public delete(id: string): void {
    this._toggleLoading();
    this._statementService.delete(id)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Extrato removido',
            life: 3000
          });
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao atualizar um lançamento no extrato',
          detail: error.error.message,
          life: 3000
        }),
      })
  }
}
